import { useState } from 'react';

import { DatabaseCheck } from '@/features/HealthCheck/DatabaseCheck';
import { createFileRoute } from '@tanstack/react-router';
import {
    Activity,
    Clock,
    Database,
    RefreshCw,
    Server,
    Shield,
    Workflow,
    Flag,
} from 'lucide-react';

import { useCommitInfo, useHealthCheck, useLLMModels, useFlags } from '@/lib/api';

import { GitCommitInfo } from '@/components/git-commit-info';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatDate, formatDateTime } from '@/lib/utils';

export const Route = createFileRoute('/system')({
    component: System,
});

function System() {
    const [lastRefresh, setLastRefresh] = useState(new Date());
    const {
        data: healthCheck,
        isLoading: isHealthLoading,
        refetch: refetchHealth,
    } = useHealthCheck();
    const { data: buildInfo, refetch: refetchBuild } = useCommitInfo();
    const {
        data: llmModels,
        isLoading: isRagLoading,
        refetch: refetchRag,
    } = useLLMModels();

    const { data: featureFlags, refetch: refetchFlags } = useFlags();

    const handleRefresh = () => {
        refetchHealth();
        refetchBuild();
        refetchRag();
        refetchFlags();
        setLastRefresh(new Date());
    };

    const dbStatus = isHealthLoading
        ? 'loading'
        : healthCheck?.database
          ? 'healthy'
          : 'error';
    const etlStatus = isHealthLoading
        ? 'loading'
        : healthCheck?.etl?.status === 'healthy'
          ? 'healthy'
          : 'error';
    const aiStatus = isRagLoading
        ? 'loading'
        : llmModels && llmModels.length > 0
          ? 'healthy'
          : 'error';

    const statusDot = (s: 'healthy' | 'error' | 'loading') => (
        <span
            className={`h-1.5 w-1.5 rounded-full shrink-0 ${
                s === 'healthy'
                    ? 'bg-primary'
                    : s === 'error'
                      ? 'bg-destructive'
                      : 'bg-muted-foreground/40 animate-pulse'
            }`}
        />
    );

    return (
        <div className="flex-1 min-h-full bg-muted/50" data-testid="System">
            <div className="p-8 pb-4 flex flex-col gap-4 bg-white">
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            System
                        </h1>
                        <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Last checked: {formatDateTime(lastRefresh)}
                        </p>
                    </div>
                    <Button onClick={handleRefresh} variant="outline">
                        <RefreshCw className="h-4 w-4" />
                        Refresh
                    </Button>
                </div>
            </div>

            <div className="p-8">
                <div className="grid grid-cols-4 gap-6 items-start">
                    <div className="col-span-3 space-y-6">
                        {buildInfo && buildInfo.commitSha && (
                            <GitCommitInfo
                                commitMessage={buildInfo.commitMessage}
                                commitHash={buildInfo.commitSha}
                                date={formatDate(buildInfo.commitDate)}
                                commitUrl={`https://github.com/sojidotai/dev-setup/commit/${buildInfo.commitSha}`}
                                releaseVersion={buildInfo.releaseVersion}
                                releaseDate={buildInfo.releaseDate}
                            />
                        )}

                        <DatabaseCheck
                            databaseInfo={healthCheck?.database ?? null}
                            migration={healthCheck?.migration ?? null}
                        />

                         {healthCheck?.etl && healthCheck.etl.status === 'healthy' && (
                            <Card className="p-0 overflow-hidden">
                                <CardHeader className="bg-muted/40 border-b px-4 py-3 flex flex-row items-center justify-between space-y-0">
                                    <div className="flex items-center gap-2">
                                        <Workflow className="h-4 w-4 text-muted-foreground" />
                                        <CardTitle className="text-sm font-semibold">
                                            ETL Status
                                        </CardTitle>
                                    </div>
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
                                        {healthCheck.etl.totalAssets ?? 0} Assets Defined
                                    </span>
                                </CardHeader>
                                <CardContent className="p-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div className="p-4 border rounded-lg bg-white shadow-xs">
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                Pipeline Assets
                                            </p>
                                            <p className="text-2xl font-semibold text-gray-900 mt-1">
                                                {healthCheck.etl.totalAssets ?? 0}
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                Total defined data assets in orchestration graph
                                            </p>
                                        </div>
                                        <div className="p-4 border rounded-lg bg-white shadow-xs">
                                            <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                                                System Integration
                                            </p>
                                            <p className="text-2xl font-semibold text-primary mt-1">
                                                Active
                                            </p>
                                            <p className="text-xs text-slate-500 mt-1">
                                                Connected to ETL via endpoint
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                    </div>

                    <div className="space-y-6">
                        <Card className="gap-0 py-0 overflow-hidden">
                            <CardHeader className="bg-muted/40 border-b px-4 py-3 flex-row items-center space-y-0">
                                <div className="flex items-center gap-2">
                                    <Activity className="h-4 w-4 text-muted-foreground" />
                                    <CardTitle className="text-sm font-semibold">
                                        Services
                                    </CardTitle>
                                </div>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="flex items-center justify-between px-4 py-2.5 border-b">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Server className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span>API</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            Online
                                        </span>
                                        {statusDot('healthy')}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between px-4 py-2.5 border-b">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Database className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span>Database</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {dbStatus === 'loading'
                                                ? '…'
                                                : dbStatus === 'healthy'
                                                  ? 'Connected'
                                                  : 'Error'}
                                        </span>
                                        {statusDot(dbStatus)}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between px-4 py-2.5 border-b">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Workflow className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span>ETL Service</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {etlStatus === 'loading'
                                                ? '…'
                                                : etlStatus === 'healthy'
                                                  ? 'Online'
                                                  : 'Error'}
                                        </span>
                                        {statusDot(etlStatus)}
                                    </div>
                                </div>
                                <div className="flex items-center justify-between px-4 py-2.5">
                                    <div className="flex items-center gap-2 text-sm">
                                        <Shield className="h-3.5 w-3.5 text-muted-foreground" />
                                        <span>AI Models</span>
                                    </div>
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs text-muted-foreground">
                                            {aiStatus === 'loading'
                                                ? '…'
                                                : aiStatus === 'healthy'
                                                  ? `${llmModels!.length} models`
                                                  : 'No key'}
                                        </span>
                                        {statusDot(aiStatus)}
                                    </div>
                                </div>
                            </CardContent>
                        </Card>

                        {featureFlags && featureFlags.filter(f => f.enabled).length > 0 && (
                            <Card className="gap-0 py-0 overflow-hidden">
                                <CardHeader className="bg-muted/40 border-b px-4 py-3 flex-row items-center space-y-0">
                                    <div className="flex items-center gap-2">
                                        <Flag className="h-4 w-4 text-muted-foreground" />
                                        <CardTitle className="text-sm font-semibold">
                                            Active Feature Flags
                                        </CardTitle>
                                    </div>
                                </CardHeader>
                                <CardContent className="p-0">
                                    {featureFlags
                                        .filter(f => f.enabled)
                                        .map((f, index, arr) => (
                                            <div
                                                key={f.name}
                                                className={`text-sm text-slate-700 px-4 py-2.5 ${
                                                    index < arr.length - 1 ? 'border-b' : ''
                                                }`}
                                            >
                                                {f.name
                                                    .split('_')
                                                    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                                    .join(' ')}
                                            </div>
                                        ))}
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
